import type { ChartData } from "../types.js";
import type { CustomChartSpec, JsonObject } from "./types.js";
import { VEGA_DATASET_NAME } from "./types.js";
import { isObject } from "./json.js";

/**
 * Binds the host's dataset into a Custom chart's spec — the data contract:
 *
 * - **Vega-Lite**: the rows become the spec's top-level `data`.
 * - **Vega**: the rows fill the `values` of the data source named `table`,
 *   keeping any `format`/`transform` the spec declared on it; when the spec
 *   declares no `table`, one is prepended so marks can still read it.
 *
 * Returns a deep copy: Vega mutates the data it is handed (tuple ids; the
 * force transform links nodes to each other in place, making cycles), so it
 * must never see the persisted spec's or the dataset's own objects — a cycle
 * there would break every JSON.stringify of the Report.
 */
export function bindDataset(
  chart: CustomChartSpec,
  rows: ChartData,
): JsonObject {
  const values = rows.map((row) => ({ ...row }));
  const spec = structuredClone(chart.spec);
  if (chart.grammar === "vega-lite") {
    return { ...spec, data: { values } };
  }
  const declared = Array.isArray(spec.data) ? spec.data : [];
  let bound = false;
  const data = declared.map((entry: unknown) => {
    if (!isObject(entry) || entry.name !== VEGA_DATASET_NAME) return entry;
    bound = true;
    return { ...entry, values };
  });
  return {
    ...spec,
    data: bound ? data : [{ name: VEGA_DATASET_NAME, values }, ...data],
  };
}
